import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Snackbar } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import MaterialTableCS from '../../../common/ComponentesCS/MaterialTableCS';
import AccordionCS from '../../../common/ComponentesCS/AccordionCS';
import ProductSeriesDialog from '../components/ProductSeriesDialog';
import { useStylesGeneral } from '../../../styles/StyleComponents';
import "../../../styles/site.css";
import { ProjectsService } from '../../../common/services';
import {
    buildProductSeriesKey,
    getSeriesValue,
    normalizeProjectCompleteDetail,
    normalizeSeriesList
} from '../utils/projectCompleteDetail';

const tableActionButtonStyle = {
    backgroundColor: '#1976d2',
    color: 'white',
    padding: '5px 10px',
    fontSize: '12px',
    textTransform: 'none'
};

const tableCenteredColumnStyle = {
    textAlign: 'center'
};

const tableCenteredActionContentStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
};

const buildCreateSeriesPayload = (article, series = []) => (
    (series || []).map((serie) => ({
        IdArticulo: article?.idArticulo,
        Serie: getSeriesValue(serie)
    }))
        .filter((serie) => Boolean(serie.Serie))
);

const ProductSeries = (props) => {
    const {
        invoicesSelected,
        productSeries,
        setProductSeries,
        setProductsSelected,
        projectData
    } = props;

    const classes = useStylesGeneral();
    const [selectedInvoices, setSelectedInvoices] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState({});
    const [localProductSeries, setLocalProductSeries] = useState(productSeries || {});
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedProductForSeries, setSelectedProductForSeries] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const totalProducts = useMemo(() => (
        Object.values(selectedProducts).reduce((total, products) => total + products.length, 0)
    ), [selectedProducts]);

    const totalSeries = useMemo(() => (
        Object.values(localProductSeries).reduce((total, series) => total + (series?.length || 0), 0)
    ), [localProductSeries]);

    const showToast = useCallback((message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    }, []);

    const handleCloseSnackbar = useCallback((event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setSnackbar({ open: false, message: '', severity: 'success' });
    }, []);

    const loadProjectSeriesData = useCallback(async () => {
        if (!projectData?.idProyecto) {
            setSelectedInvoices([]);
            setSelectedProducts({});
            return;
        }

        try {
            setLoading(true);
            const response = await ProjectsService.getProjectCompleteDetail(projectData.idProyecto);
            const normalizedData = normalizeProjectCompleteDetail(
                response,
                invoicesSelected,
                projectData
            );

            setSelectedInvoices(normalizedData.invoices);
            setSelectedProducts(normalizedData.productsByInvoice);
            setLocalProductSeries(normalizedData.productSeriesMap);

            if (setProductsSelected) {
                setProductsSelected(normalizedData.productsByInvoice);
            }

            if (setProductSeries) {
                setProductSeries(normalizedData.productSeriesMap);
            }
        } catch (error) {
            showToast('No se pudo cargar la informacion de series del proyecto.', 'error');
            setSelectedInvoices([]);
            setSelectedProducts({});
        } finally {
            setLoading(false);
        }
    }, [invoicesSelected, projectData, setProductSeries, setProductsSelected, showToast]);

    useEffect(() => {
        loadProjectSeriesData();
    }, [loadProjectSeriesData]);

    const handleSeriesAction = (rowData) => {
        setSelectedProductForSeries(rowData);
        setModalOpen(true);
    };

    const getExistingSeries = (rowData) => {
        const productKey = buildProductSeriesKey(rowData.idFactura, rowData.idArticulo);
        return localProductSeries[productKey] || [];
    };

    const handleValidateSeries = async (seriesData) => {
        const seriesPayload = buildCreateSeriesPayload(seriesData.productData, seriesData.series);

        return ProjectsService.validateSeries(seriesPayload);
    };

    const handleSaveSeries = async (seriesData) => {
        const productKey = buildProductSeriesKey(seriesData.productData.idFactura, seriesData.productData.idArticulo);
        const createSeriesPayload = buildCreateSeriesPayload(seriesData.productData, seriesData.series);

        try {
            const validationResult = await ProjectsService.validateSeries(createSeriesPayload);

            if (Number(validationResult?.success) === 0) {
                showToast(validationResult?.message || 'Las siguientes series ya existen.', 'warning');
                return {
                    success: false,
                    reason: 'series_existing',
                    validationResult
                };
            }

            await ProjectsService.createSeries(createSeriesPayload);
            const normalizedSavedSeries = normalizeSeriesList(seriesData.series, seriesData.productData.idArticulo);

            const newSeries = {
                ...localProductSeries,
                [productKey]: normalizedSavedSeries
            };

            setLocalProductSeries(newSeries);
            if (setProductSeries) {
                setProductSeries(newSeries);
            }

            showToast(`Series guardadas para ${seriesData.productData.articulo}: ${normalizedSavedSeries.length} serie${normalizedSavedSeries.length === 1 ? '' : 's'}.`, 'success');
            return true;
        } catch (error) {
            showToast('No se pudieron guardar las series del articulo.', 'error');
            return false;
        }
    };

    const getSeriesCount = (rowData) => {
        const productKey = buildProductSeriesKey(rowData.idFactura, rowData.idArticulo);
        return localProductSeries[productKey]?.length || 0;
    };

    const invoiceColumns = [
        {
            field: 'factura',
            title: 'Factura',
        },
        {
            field: 'fechaActivo',
            title: 'Fecha Activo',
        },
        {
            field: 'cliente',
            title: 'Cliente',
        },
    ];

    const productColumns = [
        {
            field: 'factura',
            title: 'Factura',
            render: (rowData) => `${rowData.idFactura} - ${rowData.factura}`
        },
        {
            field: 'articulo',
            title: 'Articulo',
        },
        {
            field: 'cantidad',
            title: 'Cant. Total',
            type: 'numeric',
            editable: 'never',
        },
        {
            field: 'cantidadSeleccionada',
            title: 'Cant. Seleccionada',
            type: 'numeric',
            editable: 'never',
        },
        {
            field: 'descripcion',
            title: 'Descripcion',
            render: (rowData) => (
                <span>{rowData.descripcion || 'Sin descripcion'}</span>
            )
        },
        {
            field: 'precioUnitario',
            title: 'Precio Unitario',
            render: (rowData) => (
                <span>${parseFloat(rowData.precioUnitario || 0).toFixed(2)}</span>
            )
        },
        {
            field: 'totalProducto',
            title: 'Total Producto',
            render: (rowData) => (
                <span style={{ fontWeight: 'bold', color: '#2e7d32' }}>${parseFloat((rowData.cantidadSeleccionada || 0) * (rowData.precioUnitario || 0) || rowData.totalProducto || 0).toFixed(2)}</span>
            )
        },
        {
            field: 'series',
            title: 'Series',
            sorting: false,
            headerStyle: tableCenteredColumnStyle,
            cellStyle: tableCenteredColumnStyle,
            render: (rowData) => {
                const seriesCount = getSeriesCount(rowData);
                return (
                    <div style={tableCenteredActionContentStyle}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => handleSeriesAction(rowData)}
                            style={tableActionButtonStyle}
                        >
                            Series
                        </Button>
                        {seriesCount > 0 && (
                            <span style={{
                                fontSize: '12px',
                                color: '#28a745',
                                fontWeight: 'bold'
                            }}>
                                ({seriesCount})
                            </span>
                        )}
                    </div>
                );
            }
        },
    ];

    return (
        <div className={`${classes.root}`} style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '20px' }}>
            {/* <div style={{ display: 'flex', flexDirection: 'row', padding: '10px', gap: '15px', alignSelf: 'flex-end' }}>
                <Button
                    className="botonAzul floatLeft"
                    onClick={() => {
                        showToast(`Facturas: ${selectedInvoices.length} | Productos: ${totalProducts} | Series: ${totalSeries}`, 'info');
                    }}
                    variant="contained"
                >
                    Mostrar Data Procesada
                </Button>
            </div> */}

            <div style={{ marginBottom: '20px' }}>
                <MaterialTableCS
                    data={selectedInvoices}
                    columns={invoiceColumns}
                    title=""
                    pageSize={5}
                    showTitle={false}
                    selection={false}
                    actions={[]}
                    pageSizeOptions={[5, 10, 25, 50]}
                    toolbar={false}
                    search={true}
                    paging={true}
                    loading={loading}
                    autoHeight={true}
                />
            </div>

            {Object.entries(selectedProducts).map(([idFactura, products]) => {
                const invoice = selectedInvoices.find((inv) => `${inv.idFactura}` === `${idFactura}`);
                return (
                    <AccordionCS
                        key={`accordion-${idFactura}`}
                        title={`Factura: ${invoice?.factura || idFactura} (${products.length} productos)`}
                        descripcion={`Productos procesados de la factura ${invoice?.factura || idFactura}`}
                        defaultExpanded={true}
                    >
                        <MaterialTableCS
                            data={products}
                            columns={productColumns}
                            title=""
                            pageSize={10}
                            showTitle={false}
                            selection={false}
                            actions={[]}
                            pageSizeOptions={[5, 10, 25, 50]}
                            toolbar={false}
                            search={true}
                            paging={true}
                            loading={loading}
                            autoHeight={true}
                        />
                    </AccordionCS>
                );
            })}

            <ProductSeriesDialog
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onValidate={handleValidateSeries}
                onSave={handleSaveSeries}
                productData={selectedProductForSeries}
                existingSeries={selectedProductForSeries ? getExistingSeries(selectedProductForSeries) : []}
            />

            <Snackbar
                className="app-toast"
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default ProductSeries;
